<template>
    <div>
        <div v-if="!walletConnected" class="compass-card text-center py-4">
            <i class="fas fa-water mb-2" style="font-size: 2rem; color: var(--compass-purple);"></i>
            <p class="analytics-text-muted mb-3">Connect your wallet to view your LP positions</p>
            <wallet-button></wallet-button>
        </div>

        <div v-else>
            <div v-if="loading" class="text-center py-4">
                <div class="spinner-border text-purple" role="status"></div>
                <p class="mt-2 analytics-text-muted">Scanning protocols for positions...</p>
            </div>

            <div v-else-if="loadError" class="compass-card text-center py-4">
                <p class="text-danger mb-2">{{ loadError }}</p>
                <button class="compass-btn compass-btn--sm compass-btn--outline" @click="loadPositions">Retry</button>
            </div>

            <div v-else-if="!positions.length && !farms.length" class="compass-card text-center py-4">
                <p class="analytics-text-muted mb-2">No LP positions found for this wallet.</p>
                <p class="small analytics-text-muted">
                    Checked: Raydium, Meteora, Kamino.
                    <br>Orca Whirlpool positions are not yet supported (SDK update pending).
                </p>
            </div>

            <div v-else>
                <!-- Position Summary -->
                <div class="row mb-3">
                    <div class="col-6 col-md-3 mb-2">
                        <div class="compass-stat-card compass-stat-card--compact">
                            <div class="stat-label">Total Value</div>
                            <div class="stat-value font-mono-num">{{ formatUsd(totalValue) }}</div>
                        </div>
                    </div>
                    <div class="col-6 col-md-3 mb-2">
                        <div class="compass-stat-card compass-stat-card--compact">
                            <div class="stat-label">Positions</div>
                            <div class="stat-value font-mono-num">{{ positions.length }}</div>
                        </div>
                    </div>
                    <div class="col-6 col-md-3 mb-2">
                        <div class="compass-stat-card compass-stat-card--compact">
                            <div class="stat-label">Unclaimed Fees</div>
                            <div class="stat-value font-mono-num text-success">{{ formatUsd(totalUnclaimedFees) }}</div>
                        </div>
                    </div>
                    <div class="col-6 col-md-3 mb-2">
                        <div class="compass-stat-card compass-stat-card--compact">
                            <div class="stat-label">Protocols</div>
                            <div class="stat-value font-mono-num">{{ uniqueProtocols.length }}</div>
                        </div>
                    </div>
                </div>

                <!-- Positions List -->
                <div v-for="pos in positions" :key="pos.positionId" class="compass-card mb-2">
                    <div class="d-flex justify-content-between align-items-start flex-wrap gap-2">
                        <div>
                            <strong>{{ pos.tokenA }}/{{ pos.tokenB }}</strong>
                            <span class="compass-badge compass-badge--outline ms-2">{{ pos.protocol }}</span>
                            <span v-if="pos.poolType" class="compass-badge compass-badge--purple ms-1">{{ pos.poolType.toUpperCase() }}</span>
                            <span v-if="pos.inRange === true" class="compass-badge compass-badge--outline ms-1" style="color: var(--compass-success);">In Range</span>
                            <span v-else-if="pos.inRange === false" class="compass-badge compass-badge--outline ms-1" style="color: var(--compass-danger);">Out of Range</span>
                        </div>
                        <div class="text-end">
                            <div class="font-mono-num" style="font-weight: 600;">{{ formatUsd(pos.valueUsd) }}</div>
                        </div>
                    </div>

                    <div class="row mt-2 small">
                        <div class="col-6 col-md-3">
                            <span class="analytics-text-muted">{{ pos.tokenA }}</span><br>
                            <span class="font-mono-num">{{ formatAmount(pos.amountA) }}</span>
                        </div>
                        <div class="col-6 col-md-3">
                            <span class="analytics-text-muted">{{ pos.tokenB }}</span><br>
                            <span class="font-mono-num">{{ formatAmount(pos.amountB) }}</span>
                        </div>
                        <div v-if="pos.lowerPrice != null" class="col-6 col-md-3">
                            <span class="analytics-text-muted">Range</span><br>
                            <span class="font-mono-num">{{ formatAmount(pos.lowerPrice) }} – {{ formatAmount(pos.upperPrice) }}</span>
                        </div>
                        <div class="col-6 col-md-3">
                            <span class="analytics-text-muted">Unclaimed Fees</span><br>
                            <span class="font-mono-num text-success">{{ formatUsd(pos.unclaimedFeesUsd) }}</span>
                        </div>
                    </div>

                    <!-- Rewards -->
                    <div v-if="pos.unclaimedRewards && pos.unclaimedRewards.length" class="mt-2 small">
                        <span class="analytics-text-muted">Pending Rewards:</span>
                        <span v-for="r in pos.unclaimedRewards" :key="r.mint" class="ms-2 font-mono-num">
                            {{ formatAmount(r.amount) }} {{ r.token }}
                        </span>
                    </div>

                    <!-- Actions -->
                    <div class="d-flex gap-2 mt-2">
                        <button class="compass-btn compass-btn--sm compass-btn--outline" :disabled="actionLoading" @click="claimFees(pos)">
                            Claim Fees
                        </button>
                        <button class="compass-btn compass-btn--sm compass-btn--outline" :disabled="actionLoading" @click="withdraw(pos, 100)">
                            Withdraw All
                        </button>
                        <button class="compass-btn compass-btn--sm compass-btn--outline" :disabled="actionLoading" @click="withdraw(pos, 50)">
                            Withdraw 50%
                        </button>
                    </div>
                </div>

                <!-- Farming Positions -->
                <div v-if="farms.length" class="mt-4">
                    <h5 style="font-weight: 600;">Farming Positions</h5>
                    <div v-for="farm in farms" :key="farm.farmId" class="compass-card mb-2">
                        <div class="d-flex justify-content-between align-items-center">
                            <div>
                                <strong>{{ farm.protocol }}</strong> Farm
                                <span class="compass-badge compass-badge--purple ms-1">{{ formatUsd(farm.stakedValueUsd) }}</span>
                            </div>
                            <div class="d-flex gap-2">
                                <button class="compass-btn compass-btn--sm compass-btn--primary" :disabled="actionLoading" @click="harvest(farm)">
                                    Harvest
                                </button>
                                <button class="compass-btn compass-btn--sm compass-btn--outline" :disabled="actionLoading" @click="unstake(farm)">
                                    Unstake
                                </button>
                            </div>
                        </div>
                        <div v-if="farm.pendingRewards && farm.pendingRewards.length" class="mt-2 small">
                            <span class="analytics-text-muted">Pending:</span>
                            <span v-for="r in farm.pendingRewards" :key="r.mint" class="ms-2 font-mono-num text-success">
                                {{ formatAmount(r.amount) }} {{ r.token }} ({{ formatUsd(r.valueUsd) }})
                            </span>
                        </div>
                    </div>
                </div>
                <!-- Orca notice -->
                <div class="mt-3 small analytics-text-muted">
                    <i class="fas fa-info-circle me-1"></i>
                    Showing positions from Raydium, Meteora, and Kamino.
                    Orca Whirlpool position scanning requires an SDK update.
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { useSolSdk } from '../../composables/useSolSdk';
import { formatUSD } from '../../composables/useFormatters';

export default {
    name: 'LpPositions',
    setup() {
        const { getSdk, isReady } = useSolSdk();
        return { getSdk, sdkReady: isReady };
    },
    data() {
        return {
            walletConnected: false,
            positions: [],
            farms: [],
            loading: false,
            loadError: null,
            actionLoading: false,
        };
    },
    computed: {
        walletAddress() {
            return window.wallet?.publicKey?.value?.toBase58();
        },
        totalValue() {
            return this.positions.reduce((sum, p) => sum + (p.valueUsd || 0), 0);
        },
        totalUnclaimedFees() {
            return this.positions.reduce((sum, p) => sum + (p.unclaimedFeesUsd || 0), 0);
        },
        uniqueProtocols() {
            return [...new Set(this.positions.map(p => p.protocol))];
        },
    },
    mounted() {
        this.walletConnected = !!window.wallet?.publicKey?.value;
        if (this.walletConnected) {
            this.loadPositions();
        }
        // Watch for wallet connect/disconnect reactively
        if (window.wallet?.publicKey) {
            this.$watch(
                () => window.wallet.publicKey.value,
                (newVal) => {
                    this.walletConnected = !!newVal;
                    if (newVal) this.loadPositions();
                    else { this.positions = []; this.farms = []; }
                }
            );
        } else {
            setTimeout(() => {
                if (window.wallet?.publicKey) {
                    this.$watch(
                        () => window.wallet.publicKey.value,
                        (newVal) => {
                            this.walletConnected = !!newVal;
                            if (newVal) this.loadPositions();
                            else { this.positions = []; this.farms = []; }
                        }
                    );
                }
            }, 500);
        }
    },
    methods: {
        async loadPositions() {
            if (!this.walletAddress || this.loading) return;
            this.loading = true;
            this.loadError = null;
            try {
                const sdk = await this.getSdk();
                const [positions, farms] = await Promise.all([
                    sdk.lp.getPositions(this.walletAddress).catch(e => {
                        console.warn('[LpPositions] getPositions error:', e.message || e);
                        return [];
                    }),
                    sdk.lp.getFarms(this.walletAddress).catch(e => {
                        console.warn('[LpPositions] getFarms error:', e.message || e);
                        return [];
                    }),
                ]);
                this.positions = Array.isArray(positions) ? positions : [];
                this.farms = Array.isArray(farms) ? farms : [];
            } catch (e) {
                console.error('[LpPositions] Failed to load', e);
                this.loadError = 'Failed to load positions: ' + (e.message || 'Unknown error');
            } finally {
                this.loading = false;
            }
        },
        async claimFees(pos) {
            this.actionLoading = true;
            try {
                const sdk = await this.getSdk();
                await sdk.lp.claimFees(this.walletAddress, pos.positionId, pos.protocol);
                alert('Fees claimed!');
                await this.loadPositions();
            } catch (e) {
                alert('Claim failed: ' + (e.message || e));
            } finally {
                this.actionLoading = false;
            }
        },
        async withdraw(pos, percent) {
            this.actionLoading = true;
            try {
                const sdk = await this.getSdk();
                await sdk.lp.withdraw(this.walletAddress, {
                    positionId: pos.positionId,
                    percent,
                    close: percent === 100,
                }, pos.protocol);
                alert('Withdrawal successful!');
                await this.loadPositions();
            } catch (e) {
                alert('Withdraw failed: ' + (e.message || e));
            } finally {
                this.actionLoading = false;
            }
        },
        async harvest(farm) {
            this.actionLoading = true;
            try {
                const sdk = await this.getSdk();
                await sdk.lp.farmHarvest(this.walletAddress, farm.farmId, farm.protocol);
                alert('Harvest successful!');
                await this.loadPositions();
            } catch (e) {
                alert('Harvest failed: ' + (e.message || e));
            } finally {
                this.actionLoading = false;
            }
        },
        async unstake(farm) {
            this.actionLoading = true;
            try {
                const sdk = await this.getSdk();
                await sdk.lp.farmUnstake(this.walletAddress, farm.positionId || farm.farmId, farm.farmId, farm.protocol);
                alert('Unstake successful!');
                await this.loadPositions();
            } catch (e) {
                alert('Unstake failed: ' + (e.message || e));
            } finally {
                this.actionLoading = false;
            }
        },
        formatUsd(val) {
            return formatUSD(parseFloat(val) || 0);
        },
        formatAmount(val) {
            if (!val && val !== 0) return '0';
            const num = parseFloat(val);
            if (num >= 1e6) return (num / 1e6).toFixed(2) + 'M';
            if (num >= 1e3) return (num / 1e3).toFixed(1) + 'K';
            if (num < 0.01 && num > 0) return num.toFixed(6);
            return num.toFixed(4);
        },
    },
};
</script>
